import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import { MdOpenInNew } from "react-icons/md";
import { Link } from "react-router-dom";
import TableComponent from "../../../components/table/TableComponent";
import { TuitionRequestInterface } from "../../../interfaces/tuitionRequest.interface";
import { Column } from "../../../interfaces/table.interface";
import { formatDateTime } from "../../../utils/date&time.util";
import CoinsDisplay from "../CoinsDisplay";
import { getTuitionRequests } from "../../../redux/slices/TuitionRequestSlice";
import { DispatchType } from "../../../interfaces/redux.interface";
import { RootState } from "../../../redux/store";
import { startLoading, stopLoading } from "../../../redux/slices/WindowSlice";

const GeneralRequest = () => {
  const dispatch: DispatchType = useDispatch();
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const { generalTuitionRequests, pagination } = useSelector(
    (state: RootState) => state.tuitionRequest
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    if (!signedAdmin?.id) return;
    dispatch(startLoading());
    dispatch(
      getTuitionRequests({
        signedAdmin,
        listType: "general",
        page: page + 1,
        limit: rowsPerPage,
      })
    ).finally(() => dispatch(stopLoading()));
  }, [dispatch, signedAdmin, page, rowsPerPage]);

  const filteredRequests = generalTuitionRequests?.filter((request) => {
    const searchText = search.toLowerCase();
    if (!searchText) return true;
    const formattedDate = formatDateTime(request.createdAt)?.toLowerCase() || "";
    return (
      request.parent?.name?.toLowerCase().includes(searchText) ||
      request.student?.name?.toLowerCase().includes(searchText) ||
      request.preferredSubjects?.some((subject) =>
        subject.toLowerCase().includes(searchText)
      ) ||
      formattedDate.includes(searchText)
    );
  });

  const columns: Column[] = [
    {
      header: "Parent",
      align: "left",
      render: (_value: any, row: TuitionRequestInterface) => row?.parent?.name,
    },
    {
      header: "Student",
      render: (_value: any, row: TuitionRequestInterface) => row?.student?.name,
    },
    {
      select: "preferredSubjects",
      header: "Preferred Subjects",
      render: (value: string[]) => value?.join(", ") || "—",
    },
    {
      header: "Date & Time",
      render: (_value: any, row: TuitionRequestInterface) =>
        formatDateTime(row?.createdAt),
    },
    {
      select: "requiredNumberOfClasses",
      header: "Classes",
      align: "center",
      render: (_value, row: TuitionRequestInterface) =>
        row.totalNumberOfClasses ?? row.requiredNumberOfClasses ?? "—",
    },
    {
      select: "gcChargePerClass",
      header: "GC / class",
      align: "right",
      render: (_value, row: TuitionRequestInterface) => (
        <CoinsDisplay
          value={
            row.parentShadowWallet?.gcChargePerClass ?? row.gcChargePerClass
          }
        />
      ),
    },
    {
      select: "totalCoins",
      header: "Total GC",
      align: "right",
      render: (_value, row: TuitionRequestInterface) => (
        <CoinsDisplay
          value={
            row.parentShadowWallet?.totalCoinsAllocated ?? row.totalCoins
          }
        />
      ),
    },
    {
      select: "currentCoins",
      header: "Current GC",
      align: "right",
      render: (_value, row: TuitionRequestInterface) => (
        <CoinsDisplay value={row.parentShadowWallet?.currentCoins} />
      ),
    },
    {
      select: "_id",
      header: "Details",
      render: (value: string) => (
        <Link
          to={`/request/${value}`}
          style={{ textDecoration: "none" }}
          className="flex"
        >
          <IconButton
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#1976d2",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              margin: "auto",
            }}
          >
            <MdOpenInNew size={20} />
          </IconButton>
        </Link>
      ),
    },
  ];

  return (
    <div className="">
      <div className="px-4 py-2">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search requests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <TableComponent
        columns={columns}
        data={filteredRequests || []}
        serverSidePagination={{
          page,
          rowsPerPage,
          count: pagination.general.totalResults,
          rowsPerPageOptions: [15, 25, 40, 100],
          onPageChange: (newPage) => setPage(newPage),
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      />
    </div>
  );
};

export default GeneralRequest;
